import { useEffect } from 'react';
import connect from 'react-redux/es/connect/connect';
import { push } from 'connected-react-router';
import { compose } from 'redux';
import PageLayout from '@uhg-uicl/ui-core/dist/PageLayout';
import { Toast, ToastAnimation } from '@uhg-uicl/ui-core/dist';
import integrations from '../../integrations';
import injectReducer from '../../utils/injectReducer';
import reducer from './modules/reducer';
import { APP_ID, ON_SINGLE_GROUP_SELECT } from './modules/constants';
import {
  makeSelectMasthead,
  makeSelectNotifications,
  makeSelectUser,
  makeSelectOpenAnimationActive,
  makeSelectCloseAnimationActive,
  makeSelectNotificationVisible,
  makeSelectNotificationMessage,
  makeSelectNotificationType,
} from './modules/selectors';
import styled from 'styled-components/macro';
import {
  onAvatarMenuSelect,
  onInitialNotifications,
  onMarkNotificationsRead,
  onUnprocessedNotifications,
  onUpdateNotifications,
  setNotificationBanner,
  setUser,
  setNavMenuItems,
  getAssigneeList,
  getAppealStatuses,
  getUserConfigs,
} from './modules/actions';
import { BaseRoute } from 'constants/routes';
import { useAuth } from 'react-oidc-context';
import { LEFT_NAV_INDEX, NAV_MENU_ID_ENUM } from 'constants/enums';
import PrivacyFooter from './components/PrivacyFooter';
import { UserProfile } from 'containers/Security/modules/types';
import { getLoggedInUser } from 'containers/Security/modules/utils';
import { useLocation } from 'react-router-dom';
import { userHasPermission } from 'commonResources/utils';
import { PERMISSIONS } from 'containers/Security/modules/enums';

type PageWrapperProps = {
  navigationPaneContent: any;
  listPaneContent: any;
  detailPaneContent: any;
  user: UserProfile;
  mastheadConfig: any;
  mastheadActions: any;
  networkSuccessNotification: boolean;
  dispatchSetNotificationBanner: Function;
  openAnimationActive: boolean;
  closeAnimationActive: boolean;
  notificationVisible: boolean;
  notificationMessage: string;
  notificationType: 'positive' | 'negative';
  dispatchSetUser: Function;
  isDocumentsPage: boolean;
  dispatchSetNavMenuItems: Function;
  dispatchGetAppealStatuses: Function;
  dispatchGetUserConfigs: Function;
};

const StyledPageWrapper = styled.div`
  #notification-banner {
    position: fixed;
    bottom: 40px;
    left: 10px;
  }
  [class*='MainContentWrapper'] {
    min-height: calc(100vh - 87px) !important;
  }
  .os-host {
    height: calc(100vh - 87px);
  }
  .os-scrollbar-vertical {
    display: ${(props) => (props.isDocumentsPage ? `auto` : `none`)};
  }
`;

const PageWrapper = ({
  navigationPaneContent = null,
  listPaneContent = null,
  detailPaneContent = null,
  user,
  mastheadConfig,
  mastheadActions,
  dispatchSetNotificationBanner,
  openAnimationActive,
  closeAnimationActive,
  notificationVisible,
  notificationMessage,
  notificationType,
  dispatchSetUser,
  dispatchSetNavMenuItems,
  isDocumentsPage = false,
  dispatchGetAppealStatuses,
  dispatchGetUserConfigs,
}: PageWrapperProps) => {
  const animationTime = 5000;
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (notificationVisible) {
      setTimeout(() => {
        dispatchSetNotificationBanner(false);
      }, 5000);
    }
  }, [notificationVisible]);

  useEffect(() => {
    dispatchSetNavMenuItems(getLoggedInUser(auth));
  }, []);

  useEffect(() => {
    const pathname = location?.pathname ?? '';
    const isPrivacySecurityPage =
      pathname.includes('ui/privacy-notice') || pathname.includes('ui/terms-of-use');
    if (!isPrivacySecurityPage) {
      dispatchGetUserConfigs('');
    }

    const isMainPage =
      pathname.includes('ui/claims') ||
      pathname.includes('ui/appeals') ||
      pathname.includes('ui/inquiries') ||
      pathname.includes('ui/my-recent-activities') ||
      pathname.includes('tab=appeals');
    if (isMainPage) {
      // dispatchGetAssigneeList(0); // Note: Moved it to be called only when needed
      if (user?.permissions && userHasPermission(PERMISSIONS.APPEAL_QUEUE_READ, user.permissions)) {
        dispatchGetAppealStatuses();
      }
    }
  }, [auth, location, user]);

  useEffect(() => {
    if (!user?.given_name || user?.given_name === 'TestUserNotLoggedIn') {
      dispatchSetUser(getLoggedInUser(auth));
      // dispatchSetNavMenuItems(getLoggedInUser(auth));
    }
  }, [user]);

  const StyledToastAnimation = styled(ToastAnimation)`
    z-index: 1000;
  `;

  return (
    <StyledPageWrapper isDocumentsPage={isDocumentsPage}>
      <PageLayout
        mastheadProps={{
          ...mastheadConfig,
          ...mastheadActions,
          productNameWidth: 500,
          onNavMenuSelect: (event, { activeItem }) => {
            mastheadActions.onNavMenuSelect(event, activeItem);
          },
          onProductMenuSelect: (event, { activeItem }) => {
            mastheadActions.onProductMenuSelect(event, activeItem);
          },
          onAvatarMenuSelect: (event, { activeItem }) => {
            mastheadActions.onAvatarMenuSelect(event, activeItem);
          },
          // showNotifications: integrations.notifications && isAuthenticated(),
          // notificationAlerts: notifications,
          // onDismissCard: (notification) => markReadRef.current([notification]),
          // notificationsCallbacks: {
          //   markAllAsRead: () => markReadRef.current(notifications),
          // },
          userFirstName: auth.user?.profile?.given_name ?? user.given_name,
          userLastName: auth.user?.profile?.family_name ?? user.family_name,
          userEmail: auth.user?.profile?.email ?? user.email,
          integrations,
          appId: APP_ID,
        }}
        navigationPaneContent={navigationPaneContent}
        listPaneContent={listPaneContent}
        detailPaneContent={detailPaneContent}
        detailPaneExpandWidth="70%"
        dataTestId="page-layout-testid"
      />
      {notificationVisible && (
        <StyledToastAnimation
          animationIn={openAnimationActive}
          animationOut={closeAnimationActive}
          animationTime={animationTime}
          domID="notification-banner"
          dataTestId={`notification-banner-${notificationType === 'positive' ? 'success' : 'fail'}`}
        >
          <Toast
            toastType={notificationType}
            headerText={notificationType === 'positive' ? 'Success' : 'Fail'}
            description={notificationMessage}
          />{' '}
        </StyledToastAnimation>
      )}
      <PrivacyFooter />
    </StyledPageWrapper>
  );
};

const mapDispatchToProps = (dispatch) => ({
  mastheadActions: {
    onLogOutSelect: () => dispatch(push(`${BaseRoute}/logout`)),
    notificationsCallbacks: {
      markAllAsRead: () => false,
      onPrevious: () => false,
      onNext: () => false,
    },
    onNavMenuOpenClose: () => false,
    onProductMenuOpenClose: () => false,
    onSupportMenuOpenClose: () => false,
    supportMenuSearch: () => false,
    onAvatarMenuOpenClose: () => false,
    onNavMenuSelect: (event, item) => {
      if (item.id == NAV_MENU_ID_ENUM.claims) {
        dispatch({
          type: ON_SINGLE_GROUP_SELECT,
          state: {
            selectedIndex: LEFT_NAV_INDEX.CLAIMS_PREPAY,
          },
        });
      }
      dispatch(push(item.path));
    },
    onProductMenuSelect: (event, item) => {
      // put any state cleanup or analytics here
      window.location.href = item.path;
    },
    onAvatarMenuSelect: (event, item, state) => {
      dispatch(onAvatarMenuSelect(state));
      if (item.path) {
        dispatch(push(item.path));
      }
    },
  },
  dispatchSetNotificationBanner: (
    notificationVisible: boolean,
    notificationMessage: string,
    notificationType: string,
  ) => dispatch(setNotificationBanner(notificationVisible, notificationMessage, notificationType)),
  dispatchOnInitialNotifications: (notifications) =>
    dispatch(onInitialNotifications(notifications)),
  dispatchOnUpdateNotifications: (notifications) => dispatch(onUpdateNotifications(notifications)),
  dispatchOnUnprocessedNotifications: (notifications) =>
    dispatch(onUnprocessedNotifications(notifications)),
  markNotificationsRead: (notifications) => dispatch(onMarkNotificationsRead(notifications)),
  dispatchSetUser: (user: any) => dispatch(setUser(user)),
  dispatchGetAppealStatuses: () => dispatch(getAppealStatuses()),
  dispatchGetUserConfigs: (cfgName: string) => dispatch(getUserConfigs(cfgName)),
  dispatchSetNavMenuItems: (user: any) => dispatch(setNavMenuItems(user)),
});

const mapStateToProps = (state) => {
  const mastheadConfig = makeSelectMasthead()(state);
  const user = makeSelectUser()(state);
  const notifications = makeSelectNotifications()(state);
  const openAnimationActive = makeSelectOpenAnimationActive()(state);
  const closeAnimationActive = makeSelectCloseAnimationActive()(state);
  const notificationVisible = makeSelectNotificationVisible()(state);
  const notificationMessage = makeSelectNotificationMessage()(state);
  const notificationType = makeSelectNotificationType()(state);
  return {
    ...state,
    mastheadConfig,
    user,
    notifications,
    openAnimationActive,
    closeAnimationActive,
    notificationVisible,
    notificationMessage,
    notificationType,
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({ key: 'app', reducer });
//@ts-ignore
export default compose(withReducer, withConnect)(PageWrapper);
